<template>
    <v-input v-bind="{...$props, ...$attrs}" :value="value" label="">
        <div class="container">
            <input type="file" ref="files" class="d-none" multiple @change="loadFiles" />

            <div
                v-if="multiple || !files.length"
                class="drag-area"
                :class="{ 'no-clickable': disabled, 'no-draggable': disabled && isDragging, 'drag-active': isDragging }"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
                @click="fileAction"
            >
                <v-icon class="mr-2">mdi-file</v-icon>
                <div class="label-content text-center">
                    <p>{{ label }}</p>
                    <p><small>Formatos permitidos: {{ allowFileFormats.join(', ') }}</small></p>
                </div>
            </div>

            <div v-if="!hideLoadedFiles" style="width: 100%;" class="d-flex flex-wrap">
                <v-card
                    v-for="(file, index) in files"
                    :key="index"
                    class="blue darken-1 ma-1 px-2 d-flex align-center justify-space-between white--text"
                    :class="{ 'full-width': !multiple }"
                >
                    <p class="my-0 d-flex align-center">
                        <v-icon v-bind="$attrs" class="white--text mr-2">{{ fileIcon(file.name) }}</v-icon>
                        {{ file.name }}
                    </p>
                    <v-btn :loading="loading" :disabled="disabled" class="ml-2" icon @click="deleteFile(index)">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-card>
            </div>
        </div>

        <KEditor
            :dialog="showDialog"
            :loadedImages="toEditImages"
            @closeDialog="closeDialog"
            @sendImages="saveEditImages"
        />
    </v-input>
</template>

<script>
import KEditor from '@/pages/Sinister/File/PhotosEditor/index'
import { readImage, reduceImageQuality, iterateDelayed, writeImage, fileIcon } from '@/utils'
import { cloneDeep } from 'lodash'

const IMAGE_FORMATS = ['png', 'jpg', 'jpeg']

export default {
  name: 'KFileField',
  components: { KEditor },
  inheritAttrs: true,

  props: {
    exe: { type: Function, required: false },
    label: { type: String, default: 'Cargar archivo' },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    allowFileFormats: { type: Array, default: () => ['pdf'] },
    btnClass: { type: String, default: 'info' },
    multiple: { type: Boolean, default: false },
    hideLoadedFiles: { type: Boolean, default: false },
    draggable: { type: Boolean, default: true },
    preloadedFiles: { type: Array, required: false },
    value: { required: true }
  },

  data() {
    return {
      isDragging: false,
      showDialog: false,
      files: [],
      toEditImages: [],
      fileIcon
    }
  },

  mounted() {
    this._loadPreloaded(this.preloadedFiles)
  },

  methods: {
    // ── Acciones de archivo ──────────────────────────────────────────────────

    fileAction() {
      if (this.disabled) return
      if (this.exe) return this.exe()
      this.$refs.files.multiple = this.multiple
      this.$refs.files.click()
    },

    loadFiles(e) {
      const files = e.target && e.target.files ? [...e.target.files] : []
      if (!files.length) return
      this.processFiles(this.multiple ? files : [files[0]])
    },

    onDragOver(e) {
      this.isDragging = true
      e.dataTransfer.dropEffect = 'copy'
    },

    onDragLeave() {
      this.isDragging = false
    },

    onDrop(e) {
      this.isDragging = false
      if (!this.draggable || this.disabled) return
      const files = [...e.dataTransfer.files]
      if (!files.length) return
      this.processFiles(this.multiple ? files : [files[0]])
    },

    // ── Procesamiento ────────────────────────────────────────────────────────

    processFiles(files = []) {
      if (!files.length || !files.every(f => f instanceof File)) return

      const invalid = files.some(f => !this.allowFileFormats.includes(f.name.split('.').pop().toLowerCase()))
      if (invalid) {
        this.$alertMessage({ title: 'Hay archivos de formatos no permitidos', text: `Formatos: ${this.allowFileFormats.join(', ')}` })
      }

      if (files.some(f => f.size > 12000000)) {
        this.$alertMessage({ title: 'Algunos archivos pesan más de 12 MB' })
      }

      const images = []
      const others = []

      for (const file of files) {
        const format = file.name.split('.').pop().toLowerCase()
        if (file.size > 12000000 || !this.allowFileFormats.includes(format)) continue
        IMAGE_FORMATS.includes(format) ? images.push(file) : others.push(file)
      }

      if (images.length) {
        this.loadImages(images)
      } else {
        this.files = [...this.files, ...others]
        this.$emit('input', this.multiple ? this.files : this.files[0])
      }

      this._resetInput()
    },

    loadImagesPromise(file) {
      return new Promise((resolve) => {
        let src
        readImage(file)
          .then(b64 => reduceImageQuality(b64))
          .then(b64Reduced => { src = b64Reduced; return writeImage(b64Reduced) })
          .then(fileReduced => {
            this.toEditImages.push({ file: fileReduced, originalFile: file, src, isImage: true })
            resolve()
          })
      })
    },

    loadImages(images) {
      return iterateDelayed(images, 50, this.loadImagesPromise)
        .then(() => { this.showDialog = !!this.toEditImages.length })
    },

    // ── KEditor ──────────────────────────────────────────────────────────────

    closeDialog() {
      this.showDialog = false
      this.toEditImages = []
    },

    saveEditImages(images) {
      if (!images) return
      this.files = [...this.files, ...images.map(i => i.file)]
      this.toEditImages = []
      this.showDialog = false
      this.$emit('input', this.multiple ? this.files : this.files[0])
    },

    // ── Eliminar ─────────────────────────────────────────────────────────────

    deleteFile(index) {
      if (this.multiple) {
        this.files.splice(index, 1)
        this.$emit('input', this.files)
      } else {
        this.files = []
        this.$emit('input', null)
      }
      this._resetInput()
    },

    // ── Helpers ──────────────────────────────────────────────────────────────

    _resetInput() {
      if (this.$refs.files) this.$refs.files.value = ''
    },

    _loadPreloaded(list) {
      if (!list || !list.length) return
      const files = Array.isArray(list) ? list : [list]
      if (files.every(f => f instanceof File)) {
        this.processFiles(files)
      } else {
        this.files = this.multiple ? files : [files[0]]
        this.$emit('input', this.multiple ? this.files : this.files[0])
      }
    }
  },

  watch: {
    preloadedFiles: {
      deep: true,
      handler(newValue) {
        if (!newValue || !newValue.length) { this.files = []; return }
        this._loadPreloaded(newValue)
      }
    },

    value(newValue) {
      // this.files = cloneDeep(newValue)
      if (!newValue && (!this.preloadedFiles || !this.preloadedFiles.length)) {
        this.files = []
        this._resetInput()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.drag-area {
  border-radius: 5px;
  width: 100%;
  min-height: 140px;
  background: #f4f3f9;
  color: #fe0000;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 2px dashed #0000001b;
  -webkit-user-select: none;
  user-select: none;

  .label-content p { margin: 0; }

  &:hover          { background: #dfdfdf; }
  &.drag-active    { border: 2px dashed #00ff2a; background: #a0a0a0; }
  &.no-clickable   { cursor: default; background-color: unset !important; border: unset !important; color: gray !important; }
  &.no-draggable   { border: unset !important; color: gray !important; }
}

.container {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.full-width { width: 100%; }
.v-card     { height: 50px; }
</style>
